import { useUsersQuery } from './useUsersQuery';
import { useUsersSearch } from './useUsersSearch';
import { columns } from '../tables/columns';
import { useParams } from 'react-router-dom';

// The backend builds a Cognito ListUsers filter of the form `email = "..."`,
// which is an EXACT match — a partial address matches nothing. So only send the
// keyword server-side once it is a complete address; anything shorter stays a
// client-side match over the loaded page.
const isCompleteEmail = (value: string) => /^\S+@\S+\.\S+$/.test(value.trim());

export const useUsers = (filters: Record<string, string> = {}) => {
    const { id: accountId } = useParams();

    // Separate client-side filters from server-side filters (UsersFilterInput only supports email, sub, accountId)
    const { status, ...serverFilters } = filters;

    // Merge accountId from route params into server filters
    const mergedFilters = accountId ? { ...serverFilters, accountId } : serverFilters;

    const { keyword } = useUsersSearch();

    // Push a full email address down to the API so the search finds users who are
    // not on the currently loaded page. The backend picks email/sub/accountId in
    // that priority order, so sending email while scoped to an account would widen
    // the result set beyond that account — keep the account scope authoritative.
    const isAccountScoped = 'accountId' in mergedFilters;
    const searchServerSide = !isAccountScoped && isCompleteEmail(keyword);

    const queryFilters = searchServerSide
        ? { ...mergedFilters, email: keyword.trim() }
        : mergedFilters;

    const { data: items = [], isLoading: loading } = useUsersQuery(queryFilters);

    // Client-side filtering for keyword and status. Still applied when the keyword
    // went server-side: it is a no-op for the returned exact match, and it keeps
    // name/username matching working for non-email keywords.
    const visibleItems = items.filter(item => {
        if (keyword &&
            !item.name?.toLowerCase().includes(keyword.toLowerCase()) &&
            !item.email?.toLowerCase().includes(keyword.toLowerCase()) &&
            !item.username?.toLowerCase().includes(keyword.toLowerCase())) {
            return false;
        }
        if (status && item.status !== status) {
            return false;
        }
        return true;
    });

    return {
        items,
        visibleItems,
        loading,
        columns,
    };
};
